// RsvpResultPage — v2.5.99 (auto-login via magic link)
// ─────────────────────────────────────────────────────────────────
// Public landing page that confirms a one-click RSVP from a spare-
// invite email. Coach feedback (item 9 + 10 in v2.5.97 — and the
// follow-up auto-login request in v2.5.99):
//
//   "When a spare player gets an email invite and they click IN or
//    OUT, create a magic link so they're automatically logged into
//    their account and were checked in. Same for OUT. It loads the
//    app and the browser and checks them in and then they can see
//    their status within the app itself."
//
// The flow is now:
//   1. Player taps IN or OUT in their spare-invite email
//   2. Backend /api/rsvp/spare(/spare-out)/:gameId/:userId/:token
//      verifies the RSVP token, writes game_availability, AND mints
//      a short-lived magic-login token bound to the player's userId.
//   3. Backend redirects browser here with `?...&magic=<token>`
//   4. THIS PAGE detects `magic`, POSTs it to /api/auth/magic-login,
//      stores the returned JWT in localStorage (matching the existing
//      AuthContext convention), refreshes the AuthContext via
//      loadUser(), then navigates to /game/:gameId.
//   5. The user lands on the game page already logged in and sees
//      their (now updated) RSVP status.
//
// FAILURE MODES
//   • Magic exchange fails (network blip, magic expired, etc):
//     fall back to the confirmation UI with an "Open in the app"
//     CTA that takes the user through the regular login flow.
//   • Backend returned a non-ok status (expired RSVP token, game
//     not found, server error): show the matching variant with
//     no auto-login attempted.
//
// SECURITY
//   The magic token is single-purpose and short-lived (5 min). It's
//   visible in the URL (browser history, Referer) — same constraint
//   as the existing RSVP token. Threat model is unchanged: anyone
//   with email access can already do password reset, so a 5-minute
//   auto-login window doesn't raise the bar.

import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, AlertCircle, ArrowRight } from 'lucide-react';
import api from '../services/api.js';
import { useAuth } from '../context/AuthContext.jsx';

export default function RsvpResultPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser, loadUser } = useAuth();
  const action = params.get('action') || 'in';
  const status = params.get('status') || 'ok';
  const gameId = params.get('gameId') || '';
  // 2026-07-20 (RSVP magic-login nav-gutted bug) — teamId now rides along
  // from buildResultRedirect (backend/src/routes/rsvp.js). See its use
  // below: we pin it as the active team BEFORE navigating into the app.
  const teamId  = params.get('teamId') || '';
  const teamName = params.get('team') || '';
  const magic   = params.get('magic')  || '';

  // We track the magic-login attempt: 'idle' (no magic), 'pending'
  // (calling API), 'redirecting' (success, about to navigate), or
  // 'failed' (fall through to manual login). The "redirecting"
  // state shows a brief "You're in! Loading your game…" flash so
  // the user has visual confirmation before the navigate fires.
  const [magicState, setMagicState] = useState(magic ? 'pending' : 'idle');
  // useRef to ensure the magic exchange only fires once even if
  // React StrictMode double-mounts the effect. Without this, a
  // double-call would 401 the second time (token already consumed
  // by the first — once we add single-use tracking) or just waste
  // a network round trip.
  const magicAttempted = useRef(false);

  useEffect(() => {
    // Only run when:
    //   - we have a magic token in the URL
    //   - the backend status indicates the RSVP was successfully
    //     recorded (any of: 'ok', 'already', 'full', 'waitlisted')
    //     — all mean "log them in and navigate them to the game"
    //   - we haven't already tried (StrictMode guard)
    //
    // v2.5.145: 'full' and 'waitlisted' added to the success-y set.
    // Previously the magic exchange was gated only to 'ok'/'already'
    // — but the weekly-reminder RSVP flow can produce 'waitlisted'
    // (team auto-routes spares to waitlist) or 'full' (cap hit
    // before tap landed). In both cases the RSVP IS recorded,
    // just not as 'in' — the user should still be auto-logged-in
    // and taken to the game page.
    if (!magic) return;
    // v2.5.161 — 'pay_required' (spare tapped "I'll Play!" on a team
    // that requires payment) is success-y: the RSVP IS recorded as
    // pending_payment, and we WANT the magic-login so they land on the
    // game page logged in and see the Pay Now CTA.
    const successy = (status === 'ok' || status === 'already' || status === 'full' || status === 'waitlisted' || status === 'pay_required');
    if (!successy) {
      setMagicState('idle'); // backend errored — don't try magic
      return;
    }
    if (magicAttempted.current) return;
    magicAttempted.current = true;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post('/auth/magic-login', { token: magic });
        if (cancelled) return;
        // Match the AuthContext.login() flow exactly so any other
        // hooks reading from localStorage see consistent state.
        // Order: refresh token FIRST, then access (matches the
        // v2.5.71 race-fix in api.js — comment there explains why).
        localStorage.setItem('refreshToken', data.refreshToken);
        localStorage.setItem('token',        data.accessToken);
        // 2026-06 — set auth state DIRECTLY from the magic-login response.
        // We used to call loadUser() here, but that does a SECOND /auth/me
        // round-trip and — critically — loadUser's catch WIPES both tokens
        // on ANY failure. On flaky mobile networks / email in-app browsers
        // (iOS Mail, Outlook, etc.) one hiccup on that call left the user
        // with no session and bounced them to /login despite a successful
        // magic exchange — the reported "I clicked I'm In but had to log in
        // anyway" bug. The magic-login response already includes the user,
        // so use it: no extra network call, no token-wipe footgun.
        if (data.user) updateUser(data.user);
        if (cancelled) return;
        // 2026-07-20 (RSVP magic-login nav-gutted bug) — ROOT CAUSE #2.
        // pickActiveTeam() (utils/lastTeam.js) prefers localStorage
        // 'gd:lastTeamId' over the server's saved lastActiveTeamId. Without
        // setting it here, a multi-team user landing on THIS game (from the
        // email) kept whatever team they'd last viewed in a PRIOR session —
        // wrong team active, or (combined with the AuthContext bootstrap
        // race fixed alongside this) an empty team list while that raced.
        // Pin the RSVP'd game's own team as active before navigating, so
        // the layout that's about to mount picks the right team on its
        // very first GET /teams resolution — same mechanism the sidebar's
        // own team-switcher already relies on (see DesktopLayout.jsx).
        try {
          if (teamId) localStorage.setItem('gd:lastTeamId', teamId);
        } catch { /* storage unavailable — non-fatal, falls back to server pref */ }
        setMagicState('redirecting');
        // Brief delay so the success flash registers visually,
        // then navigate to the game. Replace history so back-
        // button doesn't bring them back to this exchange page.
        setTimeout(() => {
          if (!cancelled && gameId) {
            navigate(`/game/${gameId}`, { replace: true });
          }
        }, 600);
      } catch (err) {
        if (cancelled) return;
        console.error('Magic-login exchange failed:', err);
        // Fall through to confirmation UI with manual CTA.
        setMagicState('failed');
        // 2026-07-20 — AuthContext.loadUser() deliberately SKIPS its
        // bootstrap /auth/me probe while this page has a `magic` param
        // (see the comment there) so it can't race this exchange and
        // clobber a session mid-write. Now that the exchange has
        // conclusively failed, run that bootstrap SEQUENTIALLY (no
        // longer racing anything) so a user who happens to already have
        // a valid, still-logged-in session on this device isn't forced
        // through a needless re-login just because the magic link itself
        // expired/errored — same safety net the page had before, minus
        // the race.
        loadUser().catch(() => {});
      }
    })();

    return () => { cancelled = true; };
    // Effect deps: only the magic token + status matter. URL param
    // helpers are stable; navigate/loadUser are functionally
    // identity-stable per their hook contracts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [magic, status]);

  // Status-specific messaging. The backend writes the row when the
  // token is valid; non-ok states are explained here for the user.
  const variants = {
    ok: action === 'in'
      ? {
          icon: <CheckCircle size={56} style={{ color: 'var(--goal, #00e87a)' }} />,
          title: "You're in!",
          subtitle: teamName
            ? `You've been added to ${teamName} for this game.`
            : "You've been added to this game.",
          accent: '#00e87a',
        }
      : {
          icon: <CheckCircle size={56} style={{ color: 'var(--text-2, #8a96a8)' }} />,
          title: "Got it — you're out",
          subtitle: "Thanks for letting the team know. We'll find someone else.",
          accent: '#8a96a8',
        },
    already: {
      icon: <CheckCircle size={56} style={{ color: 'var(--ice, #00d4ff)' }} />,
      title: action === 'in' ? "Already checked in" : "Already marked out",
      subtitle: "We've got your response — no further action needed.",
      accent: '#00d4ff',
    },
    expired: {
      icon: <AlertCircle size={56} style={{ color: '#ffa500' }} />,
      title: 'Link expired',
      subtitle: 'This invite link is no longer valid. Open the app to RSVP, or contact your coach.',
      accent: '#ffa500',
    },
    not_found: {
      icon: <XCircle size={56} style={{ color: 'var(--penalty, #ff3b3b)' }} />,
      title: 'Game not found',
      subtitle: "We couldn't find this game — it may have been cancelled. Open the app to check.",
      accent: '#ff3b3b',
    },
    full: {
      icon: <AlertCircle size={56} style={{ color: '#ffa500' }} />,
      title: 'Roster is full',
      subtitle: "Sorry — the roster filled up before your tap landed. You've been added to the waitlist; the coach will reach out if a spot opens.",
      accent: '#ffa500',
    },
    // v2.5.145 — spare-auto-waitlist case. Distinct from `full` so
    // we don't tell the player "the roster filled up" when actually
    // this team auto-routes spares to the waitlist regardless of cap.
    waitlisted: {
      icon: <AlertCircle size={56} style={{ color: '#a78bfa' }} />,
      title: "You're on the waitlist",
      subtitle: "Thanks for checking in! This team auto-adds spares to the waitlist. Your coach will promote you if a spot opens up — you'll get a notification when you're confirmed.",
      accent: '#a78bfa',
    },
    // v2.5.161 — spare tapped "I'll Play!" on a team that charges a
    // spare fee. The RSVP is recorded as pending_payment (spot held,
    // not yet confirmed). The magic-login redirects them to the game
    // page where the Pay Now CTA completes it; this copy is the
    // fallback they'd see if the auto-login didn't fire.
    pay_required: {
      icon: <AlertCircle size={56} style={{ color: '#ff8c1a' }} />,
      title: "Almost in — payment needed",
      subtitle: teamName
        ? `Your spot with ${teamName} is being held. Open the app to pay the spare fee and lock in your place — you're not confirmed to play until payment is complete.`
        : "Your spot is being held. Open the app to pay the spare fee and lock in your place — you're not confirmed to play until payment is complete.",
      accent: '#ff8c1a',
    },
    server_error: {
      icon: <XCircle size={56} style={{ color: 'var(--penalty, #ff3b3b)' }} />,
      title: 'Something went wrong',
      subtitle: "We couldn't process your response. Please try again, or open the app to RSVP directly.",
      accent: '#ff3b3b',
    },
  };
  const v = variants[status] || variants.server_error;

  // Small spinner used during the magic-login exchange so the user
  // knows something is happening. Pure CSS, no extra dependencies.
  const Spinner = () => (
    <div
      aria-hidden
      style={{
        width: 36, height: 36, borderRadius: '50%',
        border: '3px solid rgba(255,255,255,0.12)',
        borderTopColor: 'var(--ice, #00d4ff)',
        animation: 'rsvp-spin 0.8s linear infinite',
        margin: '0 auto',
      }}
    />
  );

  // Pending magic exchange — show a loading state so the user
  // doesn't see the confirmation flash and then think nothing
  // happened. Reuses the same card chrome.
  const isLoading = magicState === 'pending' || magicState === 'redirecting';

  return (
    <div
      style={{
        // Mobile/Capacitor fix — raw 100vh (inline style, not the
        // Tailwind `min-h-screen` class that index.css already maps
        // to 100dvh) doesn't track the visual viewport on mobile
        // browsers/Capacitor. This page is reached from an email
        // link and is often opened in iOS Mail/Outlook in-app
        // browsers, which are exactly the chrome-heavy webviews
        // where 100vh misbehaves most.
        minHeight: 'var(--mobile-h, 100dvh)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg, #080a0f)', color: 'var(--text, #edf0f4)',
        padding: '24px 20px',
        fontFamily: 'var(--font-body, -apple-system, "Plus Jakarta Sans", sans-serif)',
      }}>
      <style>{`@keyframes rsvp-spin { to { transform: rotate(360deg); } }`}</style>
      <div
        style={{
          maxWidth: 480, width: '100%',
          background: 'var(--surface, #0f1218)',
          border: `1px solid ${v.accent}40`,
          borderRadius: 16,
          padding: '36px 28px',
          textAlign: 'center',
        }}>
        {/* Wordmark */}
        <div style={{
          fontFamily: 'var(--font-display, Raleway, sans-serif)',
          fontSize: 14, fontWeight: 800, letterSpacing: '0.16em',
          textTransform: 'uppercase', color: 'var(--ice, #00d4ff)',
          marginBottom: 28,
        }}>
          🏒 Game Day
        </div>

        {isLoading ? (
          <>
            <div style={{ marginBottom: 18 }}><Spinner /></div>
            <h1 style={{
              fontFamily: 'var(--font-display, Raleway, sans-serif)',
              fontSize: 22, fontWeight: 800, color: 'var(--text)',
              marginBottom: 8, lineHeight: 1.2,
            }}>
              {magicState === 'redirecting' ? "You're in — opening your game…" : 'Logging you in…'}
            </h1>
            <p style={{ fontSize: 14, color: 'var(--text-2, #8a96a8)' }}>
              One moment.
            </p>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>{v.icon}</div>
            <h1 style={{
              fontFamily: 'var(--font-display, Raleway, sans-serif)',
              fontSize: 26, fontWeight: 800, color: 'var(--text)',
              marginBottom: 10, lineHeight: 1.2,
            }}>
              {v.title}
            </h1>
            <p style={{
              fontSize: 15, color: 'var(--text-2, #8a96a8)',
              lineHeight: 1.55, marginBottom: 28,
            }}>
              {v.subtitle}
            </p>

            {/* CTA: open the game in the app. If the magic exchange
                failed (status ok but magicState='failed'), this is
                the manual escape hatch — login flow returns them
                here after sign-in. We don't gate this — it's
                optional, and the email response is already done.
                v2.5.145 — also show for waitlisted/full so the player
                can navigate to the game and see their waitlist
                status. */}
            {/* 2026-06-09 (deep-link audit) — 'expired' added: a dead
                link shouldn't be a dead END. The player can still RSVP
                by opening the game in the app (login flow intercepts
                and returns them there). */}
            {gameId && (status === 'ok' || status === 'already' || status === 'full' || status === 'waitlisted' || status === 'pay_required' || status === 'expired') && (
              <Link
                to={`/game/${gameId}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '11px 20px',
                  background: v.accent, color: '#000',
                  borderRadius: 10, fontWeight: 700, fontSize: 14,
                  textDecoration: 'none',
                  fontFamily: 'var(--font-display, Raleway, sans-serif)',
                  letterSpacing: '0.04em',
                }}>
                {status === 'pay_required' ? 'Pay & confirm in the app'
                  : status === 'expired' ? 'Open the game & RSVP in the app'
                  : 'Open in the app'} <ArrowRight size={15} />
              </Link>
            )}

            {/* Secondary CTA: open dashboard. Visible always so users
                with multiple teams or who want to verify can navigate. */}
            <div style={{ marginTop: 18 }}>
              <Link
                to="/dashboard"
                style={{
                  fontSize: 13, color: 'var(--text-2)',
                  textDecoration: 'underline',
                }}>
                Or go to your dashboard →
              </Link>
            </div>

            {/* If the magic exchange failed despite a successful
                RSVP, hint at it so the user understands the extra
                login step. */}
            {magicState === 'failed' && (status === 'ok' || status === 'already' || status === 'full' || status === 'waitlisted' || status === 'pay_required') && (
              <p style={{
                fontSize: 11, color: 'var(--text-3, #5a6378)',
                marginTop: 22, lineHeight: 1.5,
              }}>
                We couldn't log you in automatically. Tap "Open in the app" above and sign in to see your updated RSVP.
              </p>
            )}
            {magicState !== 'failed' && (
              <p style={{
                fontSize: 11, color: 'var(--text-3, #5a6378)',
                marginTop: 28, lineHeight: 1.5,
              }}>
                Need to change your response? Open the app and update your RSVP for this game.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
